"use client";

import { E164Number } from "libphonenumber-js/core";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import React, { useState, useEffect } from "react";
import PhoneInputWithCountrySelect, {
  isPossiblePhoneNumber,
  isValidPhoneNumber,
} from "react-phone-number-input";
import { zapierWebhookGCC } from "@/lib/utils";
import { saveAs } from "file-saver";

interface GCCFormProps {
  darkBackground?: boolean;
  submitButtonName?: string;
  brochureDownload?: boolean;
  setIsBrochureFormModelOpen?: any;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  country_code: string;
  mobile: string;
  registrant_category: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  country_code?: string;
  mobile?: string;
  registrant_category?: string;
}

interface ButtonState {
  text: string;
  disabled: boolean;
  show: boolean;
}

const GCCForm: React.FC<GCCFormProps> = ({
  darkBackground = false,
  brochureDownload = false,
  submitButtonName = "Register Now",
  setIsBrochureFormModelOpen,
}) => {
  const initialState: FormData = {
    name: "",
    email: "",
    phone: "",
    country_code: "",
    mobile: "",
    registrant_category: "",
  };

  const btnInitalData: ButtonState = {
    text: submitButtonName,
    disabled: false,
    show: true,
  };

  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>(initialState);
  const [formBtnText, setFormBtnText] = useState<ButtonState>(btnInitalData);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

  // Check if form was previously submitted on component mount
  useEffect(() => {
    const formSubmitted = localStorage.getItem("gccMpactFormSubmitted");
    if (formSubmitted === "true") {
      setIsFormSubmitted(true);
      setCurrentStep(2);
    }
  }, []);

  const validateField = (value: any, type: string): boolean => {
    const validRegex = (email: string) => {
      if (email.endsWith("@mileseducation.com")) {
        return /^\w+([\.+-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
      }
      return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
    };

    switch (type) {
      case "name":
        return value?.trim().length >= 2;
      case "email":
        return value ? validRegex(value) : false;
      case "phone":
        return value
          ? isPossiblePhoneNumber(value) && isValidPhoneNumber(value)
          : false;
      case "registrant_category":
        return value && value !== "";
      default:
        return false;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!validateField(formData.name, "name")) {
      newErrors.name = "Please enter your full name (minimum 2 characters)";
    }

    if (!validateField(formData.email, "email")) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!validateField(formData.phone, "phone")) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!validateField(formData.registrant_category, "registrant_category")) {
      newErrors.registrant_category = "Please select an option";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    if (name === "name") {
      const nameRegex = /^[a-zA-Z\s\-'.]*$/;
      if (!nameRegex.test(value)) {
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value.trimStart(),
    }));

    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handlePhoneChange = (value?: string) => {
    if (value) {
      try {
        const phoneNumber = parsePhoneNumberFromString(value);
        if (phoneNumber && phoneNumber.isValid()) {
          const countryCode = `+${phoneNumber.countryCallingCode}`;
          const nationalNumber = phoneNumber.nationalNumber;

          setFormData((prev) => ({
            ...prev,
            phone: value,
            country_code: countryCode,
            mobile: nationalNumber,
          }));
        } else {
          setFormData((prev) => ({
            ...prev,
            phone: value ?? "",
            country_code: "",
            mobile: "",
          }));
        }
      } catch (error) {
        setFormData((prev) => ({
          ...prev,
          phone: value ?? "",
          country_code: "",
          mobile: "",
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        phone: "",
        country_code: "",
        mobile: "",
      }));
    }

    if (errors.phone) {
      setErrors((prev) => ({
        ...prev,
        phone: undefined,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const field = document.querySelector(
          `[name="${firstErrorField}"]`,
        ) as HTMLElement;
        field?.focus();
      }
      return;
    }

    setIsSubmitting(true);
    setFormBtnText((prev) => ({
      ...prev,
      text: "Submitting...",
      disabled: true,
    }));
    const payload = {
      name: formData.name,
      email: formData.email,
      country_code: formData.country_code,
      mobile: formData.mobile,
      registrant_category: formData.registrant_category,
    };

    try {
      const { error } = await zapierWebhookGCC(payload);
      if (!error) {
        if (brochureDownload) {
          const pdfUrl =
            "/newImages/MilesEducationWebsite-2024/pdf/M-PACT Invite-Attendees.pdf";
          if (pdfUrl) {
            saveAs(pdfUrl, "M-PACT Invite-Attendees.pdf");
          }
          setIsBrochureFormModelOpen(false);
        } else {
          setCurrentStep(2);
        }
        localStorage.setItem("gccMpactFormSubmitted", "true");
      }
    } catch (error) {
      console.error("Form submission failed:", error);
    } finally {
      setIsSubmitting(false);
      setFormBtnText(btnInitalData);
    }
  };

  return (
    <div>
      {isFormSubmitted || currentStep === 2 ? (
        <div className="text-center py-8">
          <div className="animate-bounce mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto text-green-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-9-9 9 9z"
              />
            </svg>
          </div>
          <h3
            className={`md:text-2xl text-xl font-bold text-center mb-2 ${darkBackground ? "text-white" : "text-[#061A2F]"}`}
          >
            Thank you for your inquiry!
          </h3>
          <h2
            className={`md:text-xl text-lg font-normal text-center ${darkBackground ? "text-gray-200" : "text-[#666]"}`}
          >
            Our programme advisor will get back to you shortly.
          </h2>
          <div className="mt-6 p-4 rounded-lg bg-green-50 border border-green-200">
            <p className="text-green-800 text-sm">
              🎯 <strong>What's next?</strong> Check your email for confirmation
              details and expect a call from our team within 24 hours.
            </p>
          </div>
        </div>
      ) : (
        /* Show form only if not submitted before */
        <form className="flex flex-col" onSubmit={handleSubmit} noValidate>
          <div className="mb-2">
            <input
              type="text"
              name="name"
              placeholder="Full Name*"
              value={formData.name}
              onChange={handleInputChange}
              required
              aria-label="Full Name"
              aria-invalid={errors.name ? "true" : "false"}
              aria-describedby={errors.name ? "name-error" : undefined}
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none transition-colors bg-white border-white `}
            />
            {errors.name && (
              <p
                id="name-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.name}
              </p>
            )}
          </div>

          <div className="mb-2">
            <input
              type="email"
              name="email"
              placeholder="E-mail*"
              value={formData.email}
              onChange={handleInputChange}
              required
              aria-label="Email Address"
              aria-invalid={errors.email ? "true" : "false"}
              aria-describedby={errors.email ? "email-error" : undefined}
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none transition-colors bg-white border-white`}
            />
            {errors.email && (
              <p
                id="email-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.email}
              </p>
            )}
          </div>

          <div className="mb-2">
            <PhoneInputWithCountrySelect
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={(value) =>
                handlePhoneChange(value as E164Number | undefined)
              }
              placeholder="Phone Number*"
              autoComplete="off"
              defaultCountry="IN"
              required
              aria-label="Phone Number"
              aria-invalid={errors.phone ? "true" : "false"}
              aria-describedby={errors.phone ? "phone-error" : undefined}
              className={`appearance-none block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none transition-colors bg-white border-white`}
            />
            {errors.phone && (
              <p
                id="phone-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.phone}
              </p>
            )}
          </div>

          <div className="mb-2">
            <select
              name="registrant_category"
              value={formData.registrant_category}
              onChange={handleInputChange}
              required
              aria-label="Select Category"
              aria-invalid={errors.registrant_category ? "true" : "false"}
              aria-describedby={
                errors.registrant_category ? "institute-error" : undefined
              }
              className={`block !text-sm md:text-base w-full md:h-[42px] lg:h-[42px] font-medium h-[42px] rounded-xl border py-3 px-4 leading-tight focus-visible:outline-none transition-colors bg-white border-white`}
            >
              <option value="" disabled>
                Student/ Professional/ Institution
              </option>
              <option value="Student">Student</option>
              <option value="Professional">Professional</option>
              <option value="University Representative">
                University Representative
              </option>
            </select>
            {errors.registrant_category && (
              <p
                id="institute-error"
                role="alert"
                className="text-red-500 text-xs mt-1 ml-1"
              >
                {errors.registrant_category}
              </p>
            )}
          </div>

          <button
            className={`font-semibold bg-[#3399FF] text-white text-xs md:text-sm py-2 capitalize rounded-[24px_24px_24px_0px] transition-all duration-200 ${
              formBtnText.disabled
                ? "opacity-70 cursor-not-allowed"
                : "hover:bg-[#2A85FF] hover:shadow-lg"
            }`}
            type="submit"
            disabled={formBtnText.disabled || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                {formBtnText.text}
              </div>
            ) : (
              formBtnText.text
            )}
          </button>
        </form>
      )}
    </div>
  );
};

export default GCCForm;
